import React from "react"
import { useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import ReCAPTCHA from "react-google-recaptcha"
import {
	Flex,
	Text,
	Rte,
	TextField,
	Radio,
	Checkbox,
	Textarea,
	Select,
	Button,
} from "@capioweb/ui-components"
import { StringProperty } from "../../@types/EpiFoundationSpa/Property"
import {
	EpiForm,
	EpiFormField,
	EpiFormOptionItem,
	ValidationResponse,
} from "../../@types/CapioWeb/epiForms"
import { PUBLIC_PAGE_API_ENDPOINT } from "../../constants"
import { BlockFullWidthLayout } from "../../utils/layout"
import { createMarkup } from "../../utils"
import { FormContainer } from "./styled"
import { getAntiforgeryToken } from '../../utils/antiforgery'

interface CapioNexusFormContainerBlockProps {
	title: StringProperty
	description: StringProperty
	epiForm: EpiForm
	anchorId?: StringProperty
}

interface FieldsetProps {
	field?: EpiFormField
	errors?: Record<string, any>
	children?: React.ReactNode
}

const Fieldset: React.FC<FieldsetProps> = ({ field, errors, children }) => {
	const error = errors[field.name]
	const helperText = errors[field.name]?.message ?? null

	return (
		<Flex flexDirection="column" as="fieldset" mb={1}>
			<Text as="legend" variant="headline-4" mb={2}>
				{field.label}
			</Text>
			{children}
			<Flex
				pl={field.type === "checkbox" ? 4 : 0}
				w={1}
				minh={helperText ? "none" : 3}
				mt={-1.25}
			>
				<Text
					as="span"
					textColor={error ? "alert" : "navy"}
					fontSize={1.75}
					lineHeight={2.5}
					mb={0}
				>
					{helperText}
				</Text>
			</Flex>
		</Flex>
	)
}

const CapioNexusFormContainerBlock: React.FC<CapioNexusFormContainerBlockProps> =
	({ epiForm, ...rest }) => {
		const {
			handleSubmit,
			register,
			setValue,
			setError,
			getValues,
			formState: { errors },
		} = useForm({
			mode: "onChange",
		})
		const navigate = useNavigate()

		const [submitDisabled, setSubmitDisabled] = React.useState(true)
		const [successMessage, setSuccessMessage] = React.useState(null)
		const [genericErrorMessages, setGenericErrorMessages] = React.useState([])

		const [captchaFieldName, setCaptchaFieldName] = React.useState(null)
		React.useEffect(() => {
			if (epiForm.fields?.length === 0) return
			const hasCaptcha = !!epiForm.fields.find(
				(field) => field.type === "captcha"
			)
			setSubmitDisabled(false)
			setCaptchaFieldName(
				hasCaptcha
					? epiForm.fields.find((field) => field.type === "captcha")?.name
					: null
			)
		}, [])

		const onCaptchaChange = (challengeResponse: string) => {
			setValue(captchaFieldName, challengeResponse)
			setSubmitDisabled(false)
		}

		const onSubmit = async (data: Record<string, string>) => {
			const url = PUBLIC_PAGE_API_ENDPOINT + epiForm.action
			const formData = new FormData()

			for (let key in data) {
				if (key === "field") {
					formData.append(key, data[key][1])
				} else {
					let value = data[key]

					formData.append(key, value ?? "")
				}
			}

			const antiForgeryToken = await getAntiforgeryToken()

			fetch(url, {
				method: epiForm.method,
				body: formData,
				credentials: "include",
				headers: {
					RequestVerificationToken: antiForgeryToken
				}
			})
				.then((res) => {
					return res
						.json()
						.then((json) => ({ success: res.status === 200, data: json }))
				})
				.then((result) => {
					if (result.success) {
						if (result.data.result.redirect) {
							navigate(result.data.result.redirect)
						} else if (result.data.result.message) {
							setSuccessMessage(result.data.result.message)
							setGenericErrorMessages([])
						}
					} else {
						const validationIssues: ValidationResponse = result.data
						Object.keys(validationIssues.errors).forEach((fieldName) => {
							const issues = result.data.errors[fieldName]

							setError(fieldName, {
								type: "manual",
								message: issues,
							})
						})
						if (result.data.errors && result.data.errors.length > 0) {
							const fieldNames = epiForm.fields.map((x) => x.name)
							const errors = result.data.errors.map((item) => {
								if (fieldNames.includes(item.key)) {
									return null
								}
								return item.message
							})
							setGenericErrorMessages(errors)
							setSuccessMessage('')
						}
					}
				})
		}

		return (
			<BlockFullWidthLayout
				id={rest.anchorId?.value || null}
				heading={rest.title}
				{...rest}
			>
				<FormContainer>
					<Flex
						as="form"
						noValidate
						action={epiForm.action}
						method={epiForm.method}
						encType={epiForm.encType}
						id={epiForm.id}
						onSubmit={handleSubmit(onSubmit)}
						flexDirection="column"
						w={1}
						maxw={57}
						m="0 auto"
					>
						{epiForm.fields.map((field, i) => {
							const isInputField = !["rte", "captcha", "submit"].includes(
								field.type
							)

							if (field && field.type && isInputField) {
								const { type, value, required, ...rest } = field
								// https://react-hook-form.com/api/useform/register
								const { ref, ...registerProps } = register(field.name, {
									required: {
										value: field.required,
										message: field.requiredMessage,
									},
									...(field.validators?.length > 0 && {
										pattern: {
											value: new RegExp(field.validators[0].pattern),
											message: field.validators[0].message,
										},
									}),
								})
								const fieldProps = {
									...registerProps,
									...rest,
									type,
									key: field.id,
									error: errors[field.name],
									...(field.type !== "hidden" &&
										errors[field.name] && {
										helperText: errors[field.name].message,
									}),
								}

								if (
									[
										"text",
										"email",
										"number",
										"date",
										"datetime-local",
									].includes(type)
								) {
									return <TextField inputRef={ref} {...fieldProps} />
								} else {
									return {
										textarea: (
											<Textarea rows={6} inputRef={ref} {...fieldProps} />
										),
										select: field.items && (
											<Select
												inputRef={ref}
												options={field.items.map((item) => ({
													label: item.caption,
													...item,
												}))}
												{...fieldProps}
											/>
										),
										radio: field.items && (
											<Fieldset field={field} errors={errors} key={field.id}>
												<Flex flexWrap="wrap">
													{field.items.map((item: EpiFormOptionItem) => {
														const { checked, ...restItem } = item

														const isLastItem =
															i === field.items.length - 1 ? true : false
														const { ref, ...rest } = register(field.name, {
															...(isLastItem && {
																required: {
																	value: field.required,
																	message: field.requiredMessage,
																},
															}),
														})

														return (
															<Flex mr={3} mb={2} key={item.value}>
																<Radio
																	inputRef={ref}
																	id={field.name + "-" + item.value}
																	name={field.name}
																	label={item.caption}
																	{...restItem}
																	{...rest}
																/>
															</Flex>
														)
													})}
												</Flex>
											</Fieldset>
										),
										checkbox:
											field.items && field.items.length > 1 ? (
												<Fieldset field={field} errors={errors} key={field.id}>
													<Flex flexDirection="column">
														{field.items.map((item: EpiFormOptionItem, i) => {
															const { checked, ...restItem } = item
															const isLastItem =
																i === field.items.length - 1 ? item : null

															const { ref, ...rest } = register(field.name, {
																...(isLastItem && {
																	required: {
																		value: field.required,
																		message: field.requiredMessage,
																	},
																}),
															})
															return (
																<Flex mb={2} key={item.value}>
																	<Checkbox
																		inputRef={ref}
																		id={field.name + "-" + item.value}
																		name={field.name}
																		label={item.caption}
																		value={item.value}
																		{...restItem}
																		{...rest}
																	/>
																</Flex>
															)
														})}
													</Flex>
												</Fieldset>
											) : (
												<Checkbox
													inputRef={ref}
													value={
														field.items?.length > 0 && field.items[0].value
													}
													{...fieldProps}
												/>
											),
										hidden: (
											<input
												ref={ref}
												readOnly
												key={field.value}
												value={field.value}
												{...fieldProps}
											></input>
										),
									}[type]
								}
							} else {
								const { type } = field
								return {
									rte: (
										<Rte key={field.id}>
											<div dangerouslySetInnerHTML={{ __html: field.value }} />
										</Rte>
									),
									captcha: (
										<Flex my={2} key={field.id}>
											<ReCAPTCHA
												sitekey={field.value}
												onChange={onCaptchaChange}
											/>
											<input
												type="hidden"
												name={field.name}
												{...register(field.name)}
											/>
										</Flex>
									),
									submit: (
										<Flex
											flexDirection={{ _: "column", m: "row" }}
											key={field.id}
											mt={2}
										>
											<Button type="submit" disabled={submitDisabled}>
												{field.label}
											</Button>
										</Flex>
									),
								}[type || null]
							}
						})}
						{/* reCaptcha error message */}
						<Flex w={1} mt={errors[captchaFieldName]?.message ? 1.5 : 0}>
							<Text
								as="span"
								textColor={errors[captchaFieldName]?.message ? "alert" : "navy"}
								variant="paragraph-14"
								mb={0}
							>
								{errors[captchaFieldName]?.message}
							</Text>
						</Flex>
						{genericErrorMessages?.length > 0 &&
							genericErrorMessages.map((error, i) => {
								return (
									<Flex w={1} mt={1.5} key={i + error.key}>
										<Text
											as="span"
											textColor="alert"
											variant="paragraph-14"
											mb={0}
										>
											{error}
										</Text>
									</Flex>
								)
							})}
						{successMessage && (
							<Flex w={1} mt={1.5}>
								<Rte as="div">
									<div dangerouslySetInnerHTML={createMarkup(successMessage)} />
								</Rte>
							</Flex>
						)}
					</Flex>
				</FormContainer>
			</BlockFullWidthLayout>
		)
	}

export { CapioNexusFormContainerBlock }
